/**
 * @file Wallet : SEUL point de lecture/ecriture des soldes (monnaies + ressources). Toute variation emet WalletChanged.
 */
import { Decimal } from '@shared/bignum';
import type { DecimalSource } from '@shared/bignum';
import type { WalletId } from '@shared/enums';
import { fail, okVoid } from '@shared/types';
import type { Result } from '@shared/types';
import type { WalletAmounts } from '@config/types';
import type { EventBus } from '../events/EventBus';
import type { GameEvents } from '../events/GameEvents';

/** Montants normalises en Decimal (cles absentes = 0). */
export type ResolvedAmounts = Partial<Record<WalletId, Decimal>>;

export const resolveAmounts = (a: WalletAmounts | undefined): ResolvedAmounts => {
  const out: ResolvedAmounts = {};
  if (!a) return out;
  for (const [k, v] of Object.entries(a) as Array<[WalletId, DecimalSource | undefined]>) {
    if (v === undefined) continue;
    out[k] = Decimal.fromValue(v);
  }
  return out;
};

export class Wallet {
  constructor(
    /** Reference vers PlayerData.wallet (mute en place). */
    private readonly balances: Record<WalletId, Decimal>,
    private readonly bus: EventBus<GameEvents>,
  ) {}

  get(id: WalletId): Decimal {
    return this.balances[id];
  }

  canAfford(cost: ResolvedAmounts): boolean {
    for (const [id, v] of Object.entries(cost) as Array<[WalletId, Decimal]>) if (this.balances[id].lt(v)) return false;
    return true;
  }

  /** Debit atomique : tout ou rien. */
  spend(cost: ResolvedAmounts, reason: string): Result {
    if (!this.canAfford(cost)) return fail('notEnoughFunds');
    for (const [id, v] of Object.entries(cost) as Array<[WalletId, Decimal]>) {
      if (v.eq(0)) continue;
      this.balances[id] = this.balances[id].sub(v);
      this.bus.emit('WalletChanged', { id, delta: v.neg(), value: this.balances[id], reason });
    }
    return okVoid;
  }

  /** Credit (montants negatifs interdits : utiliser spend). */
  grant(amounts: ResolvedAmounts, reason: string): void {
    for (const [id, v] of Object.entries(amounts) as Array<[WalletId, Decimal]>) {
      if (v.lte(0)) continue;
      this.balances[id] = this.balances[id].plus(v);
      this.bus.emit('WalletChanged', { id, delta: v, value: this.balances[id], reason });
    }
  }

  /** Remise a zero d'une monnaie (Rebirth : argent). */
  reset(id: WalletId, reason: string): void {
    const prev = this.balances[id];
    this.balances[id] = new Decimal(0);
    if (!prev.eq(0)) this.bus.emit('WalletChanged', { id, delta: prev.neg(), value: this.balances[id], reason });
  }
}
